import { forwardRef, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react'
import { cn } from '@/lib/cn'

const baseField = 'h-11 w-full rounded-md px-3.5 text-body-md text-slate-800'

export function Field({
  label,
  error,
  hint,
  required,
  children,
}: {
  label?: string
  error?: string
  hint?: string
  required?: boolean
  children: ReactNode
}) {
  return (
    <div>
      {label && (
        <label className="mb-1.5 block text-label-md uppercase tracking-wide text-slate-500">
          {label} {required && <span className="text-danger">*</span>}
        </label>
      )}
      {children}
      {hint && !error && <p className="mt-1 text-label-md text-slate-400">{hint}</p>}
      {error && <p className="mt-1 text-label-md text-danger">{error}</p>}
    </div>
  )
}

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(
  ({ className, invalid, ...rest }, ref) => (
    <input ref={ref} className={cn(baseField, invalid && 'border-danger ring-1 ring-danger', className)} {...rest} />
  ),
)
TextInput.displayName = 'TextInput'

export const TextArea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }>(
  ({ className, invalid, ...rest }, ref) => (
    <textarea
      ref={ref}
      className={cn('w-full rounded-md px-3.5 py-2.5 text-body-md text-slate-800', invalid && 'border-danger ring-1 ring-danger', className)}
      {...rest}
    />
  ),
)
TextArea.displayName = 'TextArea'

export interface Option<T extends string = string> {
  value: T
  label: string
}

export const SelectInput = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & { options: Option[]; placeholder?: string; invalid?: boolean }
>(({ className, options, placeholder, invalid, ...rest }, ref) => (
  <select ref={ref} className={cn(baseField, 'appearance-none bg-white pr-8', invalid && 'border-danger ring-1 ring-danger', className)} {...rest}>
    {placeholder && (
      <option value="" disabled>
        {placeholder}
      </option>
    )}
    {options.map((o) => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    ))}
  </select>
))
SelectInput.displayName = 'SelectInput'

export function SwitchInput({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label?: string }) {
  return (
    <label className="flex cursor-pointer items-center gap-3">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={cn('relative h-6 w-11 flex-none rounded-full transition-colors', checked ? 'bg-primary' : 'bg-slate-300')}
      >
        <span className={cn('absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all', checked ? 'left-[22px]' : 'left-0.5')} />
      </button>
      {label && <span className="text-title-sm font-medium text-slate-700">{label}</span>}
    </label>
  )
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
}: {
  options: Option<T>[]
  value: T
  onChange: (v: T) => void
}) {
  return (
    <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded-[6px] px-4 py-1.5 text-title-sm font-semibold transition-colors',
            value === o.value ? 'bg-white text-primary-dark shadow-sm' : 'text-slate-500 hover:text-slate-700',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}
